import { FiShield, FiAlertTriangle } from 'react-icons/fi';
import { IoIosRefresh } from 'react-icons/io';
import ThreeColumnLayout from '../../layouts/ThreeColumnLayout';
import FilterSidebar from '../../organisms/FilterSidebar';
import FilterSkeleton from '../../atoms/FilterSkeleton';
import ATable from '../../organisms/ATable';
import TableSkeleton from '../../atoms/TableSkeleton';
import Loading from '../../ions/Loading';
import TextEdit from '../../molecules/TextEdit';
import PageFooter from '../../molecules/PageFooter';
import CopyButton from '../../atoms/CopyButton';
import DocumentStatusBadge from './shared/DocumentStatusBadge';
import PismoStatusBadge from './shared/PismoStatusBadge';
import ReceivableItemView from './ReceivableItemView';
import { formatCurrency, formatDate, formatCpfCnpj } from '../../../utils/collateralFormatters';
import useReceivablePageList from '../../../hooks/useReceivablePageList';

const columns = [
    { id: 'op', title: 'Operação / NFe', field: 'operationCode', canSort: true, width: '200px' },
    { id: 'debtor', title: 'Sacado', field: 'debtorName', canSort: true },
    { id: 'ndup', title: 'NDup', field: 'receivableNumber', canSort: false, width: '55px' },
    { id: 'emission', title: 'Emissão', field: 'emissionDate', canSort: true, width: '85px' },
    { id: 'dueDate', title: 'Vencimento', field: 'dueDate', canSort: true, width: '90px' },
    { id: 'city', title: 'Cidade / UF', field: 'city', canSort: true, width: '130px' },
    { id: 'amount', title: 'Valor', field: 'amount', canSort: true, cssClass: 'text-right', width: '110px' },
    { id: 'status', title: 'Status', field: 'status', canSort: false, cssClass: 'text-center', width: '90px' },
    { id: 'allocation', title: 'Alocação', field: 'allocationStatus', canSort: false, cssClass: 'text-center', width: '100px' },
    { id: 'creditRight', title: 'Registro', field: 'creditRightStatus', canSort: false, cssClass: 'text-center', width: '110px' },
];

const ReceivablePageList = () => {
    const {
        rows, total, page, setPage, pageSize,
        isLoading, initialLoading,
        apiInfo, statusCounts, allocationCounts,
        statusFilter, setStatusFilter,
        allocationFilter, setAllocationFilter,
        searchInput, setSearchInput, search, setSearch,
        sortField, setSortField, sortOrder, setSortOrder,
        selectedItem, setSelectedItem,
        refresh, handleRowClick,
    } = useReceivablePageList();

    const totalAll = Object.values(statusCounts).reduce((a: number, b: any) => a + (b?.count || 0), 0);

    const filterGroups = initialLoading ? [] : [
        {
            title: '',
            children: (
                <div className="space-y-4">
                    <button onClick={() => { setStatusFilter(''); setAllocationFilter(''); }}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${!statusFilter && !allocationFilter
                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                        <div className="flex items-center justify-between">
                            <span>Todos</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{totalAll}</span>
                        </div>
                    </button>

                    {Object.keys(statusCounts).length > 0 && (
                        <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">STATUS</div>
                            <div className="space-y-1">
                                {Object.entries(statusCounts).map(([key, data]: [string, any]) => (
                                    <button key={key} onClick={() => { setStatusFilter(key); setAllocationFilter(''); }}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${statusFilter === key
                                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                        <div className="flex items-center justify-between">
                                            <span>{data.label || key}</span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">{data.count}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {Object.keys(allocationCounts).length > 0 && (
                        <div className="border-t border-gray-200 dark:border-slate-700 pt-3">
                            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 px-3">ALOCAÇÃO</div>
                            <div className="space-y-1">
                                {Object.entries(allocationCounts).map(([key, data]: [string, any]) => (
                                    <button key={key} onClick={() => { setAllocationFilter(key); setStatusFilter(''); }}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${allocationFilter === key
                                            ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'}`}>
                                        <div className="flex items-center justify-between">
                                            <span>{data.label || key}</span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">{data.count}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            ),
        },
    ];

    return (
        <>
            {isLoading && <Loading />}

            <ThreeColumnLayout
                leftSidebarTitle={
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center space-x-2">
                            <FiShield className="text-blue-600" size={18} />
                            <span>Garantias</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {apiInfo.version && <div>v{apiInfo.version}</div>}
                            <div className={`font-medium ${apiInfo.environment === 'prd' ? 'text-red-600' :
                                apiInfo.environment === 'hml' ? 'text-yellow-600' : 'text-green-600'}`}>
                                {(apiInfo.environment || '').toUpperCase()}
                            </div>
                        </div>
                    </div>
                }
                leftSidebar={
                    <FilterSidebar filterGroups={filterGroups}>
                        {initialLoading && <FilterSkeleton />}
                    </FilterSidebar>
                }
                showRightPanel={false}
                onCloseRightPanel={() => {}}
                rightPanel={null}
            >
                <div className="flex flex-col h-full bg-white dark:bg-slate-800 relative overflow-hidden">
                    <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-3 bg-white dark:bg-slate-800 z-10">
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Recebíveis</h1>
                            <div className="flex items-center space-x-2">
                                <button onClick={refresh}
                                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                    title="Atualizar lista">
                                    <IoIosRefresh size={18} />
                                </button>
                                <div className="w-80">
                                    <TextEdit label="Buscar operação, NF, NFe, sacado, cidade..." value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                        onKeyDown={(e: any) => { if (e.key === 'Enter') { setSearch(searchInput); setPage(1); } }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto [&_table]:table-fixed [&_table]:w-full">
                        {initialLoading ? (
                            <TableSkeleton rows={12} />
                        ) : (
                            <ATable headers={columns} fieldSorted={sortField} orderSorted={sortOrder}
                                onSort={(f: string, o: 'ASC' | 'DESC') => { setSortField(f); setSortOrder(o); }}>
                                {rows.length === 0 ? (
                                    <tr><td colSpan={columns.length} className="py-8 text-center text-gray-500 dark:text-gray-400">Nenhum recebível encontrado</td></tr>
                                ) : rows.map((row: any, index: number) => (
                                    <tr key={row.id}
                                        className={`group hover:bg-blue-50 dark:hover:bg-slate-700 cursor-pointer ${index % 2 === 0 ? 'bg-white dark:bg-slate-800' : 'bg-gray-50 dark:bg-slate-900'}`}
                                        onClick={() => handleRowClick(row)}>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white">
                                            <div className="font-medium">{row.operationCode} | NF {row.titleNumber}</div>
                                            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                                <span className="truncate max-w-[150px]" title={row.nfeKey}>{row.nfeKey}</span>
                                                <CopyButton data={row.nfeKey} label="Copiar NFe" className="p-0.5 text-xs" />
                                            </div>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white">
                                            <div className="truncate" title={row.debtorName}>{row.debtorName}</div>
                                            <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                                <span>{formatCpfCnpj(row.debtorDocument)}</span>
                                                <CopyButton data={row.debtorDocument} label="Copiar documento" className="p-0.5 text-xs" />
                                            </div>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white">{row.receivableNumber || '—'}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300">{formatDate(row.emissionDate)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-gray-300">{formatDate(row.dueDate)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white truncate">
                                            <div className="flex items-center gap-1">
                                                <span className="truncate">{[row.city, row.state].filter(Boolean).join(' / ') || '—'}</span>
                                                {row.ibgeCodeStatus === 'UNRESOLVED' && (
                                                    <FiAlertTriangle
                                                        className="text-amber-500 flex-shrink-0"
                                                        size={14}
                                                        title="Código IBGE não resolvido — clique para corrigir a cidade"
                                                    />
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-sm dark:text-white text-right">{formatCurrency(row.amount)}</td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <DocumentStatusBadge status={row.status} statusFormatted={row.statusFormatted || row.status} />
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center text-xs">
                                            {row.allocationStatusFormatted
                                                ? <span className="px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300 text-[10px] font-medium">{row.allocationStatusFormatted}</span>
                                                : <span className="text-gray-400">—</span>}
                                        </td>
                                        <td className="py-2 px-3 border-b border-gray-200 dark:border-slate-700 text-center">
                                            <PismoStatusBadge status={row.creditRightStatus} statusFormatted={row.creditRightStatusFormatted} />
                                        </td>
                                    </tr>
                                ))}
                            </ATable>
                        )}
                    </div>

                    <div className="flex-shrink-0 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <PageFooter currentPage={page - 1} totalPages={Math.ceil(total / pageSize)} itemsPerPage={pageSize} totalElements={total} onPageChange={(p) => setPage(p + 1)} />
                    </div>
                </div>
            </ThreeColumnLayout>

            {selectedItem && (
                <ReceivableItemView
                    item={selectedItem}
                    onClose={() => setSelectedItem(null)}
                    onUpdated={() => { setSelectedItem(null); refresh(); }}
                />
            )}
        </>
    );
};

export default ReceivablePageList;
